import {
  Coupon_DiscountValue,
  Coupon_SimpleCoupon_CouponTheme,
  Coupon_Tier,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';

import {COLORS, FONT_SIZES, FONT_WEIGHTS, Text} from '@aboutyou/ui-components';
import {usePrevious} from '@aboutyou/utils';
import React, {useRef, useState} from 'react';
import {useRectEffect} from 'react-viewport-utils';
import styled, {css} from 'styled-components';
import {CouponTiers, TierContainer, TierShape} from './CouponTiers';
import {FOOTER_HEIGHT} from './constants';

const HEADLINE_LINE_HEIGHT = 18;

interface CouponHeaderProps {
  discountValue?: Coupon_DiscountValue;
  headline: string;
  'data-testid'?: string;
  tiers?: Coupon_Tier[];
  supportsHover: boolean;
  isOnlyAppCoupon: boolean;
  couponTheme: Coupon_SimpleCoupon_CouponTheme;
  bannerText?: string;
  isDisplayMode?: boolean;
  isDisabled?: boolean;
  isHotDeal?: boolean;
}

export const CouponHeader: React.FC<CouponHeaderProps> = ({
  discountValue,
  headline,
  tiers,
  'data-testid': dataTestId,
  couponTheme,
  isOnlyAppCoupon,
  supportsHover,
  bannerText,
  isDisplayMode,
  isDisabled,
  isHotDeal,
}) => {
  const headlineRef = useRef<HTMLSpanElement>(null);
  const [isHeadlineMoreThanThreeLines, setIsHeadlineMoreThanThreeLines] =
    useState<boolean>();

  useRectEffect(
    rect => {
      setIsHeadlineMoreThanThreeLines(
        (rect?.height || 0) > HEADLINE_LINE_HEIGHT * 3,
      );
    },
    headlineRef,
    {
      disableScrollUpdates: true,
      disableDimensionsUpdates: true,
    },
    [headline],
  );

  const shouldReduceHeaderFont = usePrevious(
    isHeadlineMoreThanThreeLines,
    current => current === undefined,
  );

  const addExtraPaddingBottom =
    supportsHover && isOnlyAppCoupon && (!isHotDeal || !isDisplayMode);
  const removeFlexBasis = Boolean(isDisplayMode && isHotDeal);

  if (tiers) {
    return (
      <HeaderAndBannerWrapper
        $addExtraPaddingBottom={addExtraPaddingBottom}
        $removeFlexBasis={removeFlexBasis}
      >
        <TierHeaderWrapper
          $couponTheme={couponTheme}
          $isSingleTier={tiers.length === 1}
          $isDisplayMode={isDisplayMode}
          $isDisabled={isDisabled}
        >
          <Headline
            color="inherit"
            data-testid={`${dataTestId}Headline`}
            $removeSidePadding={tiers.length > 1}
            $isDisabled={isDisabled}
          >
            {headline}
          </Headline>
          <StyledCouponTiers
            tiers={tiers}
            $isSingleTier={tiers.length === 1}
            $isDisabled={isDisabled}
            forceActiveState={isDisplayMode}
            $isDisplayMode={isDisplayMode}
          />
        </TierHeaderWrapper>
        {bannerText && (
          <BannerText
            data-testid={`${dataTestId}BannerText`}
            $isAppOnlyCoupon={isOnlyAppCoupon}
            $isDisplayMode={isDisplayMode}
            $isDisabled={isDisabled}
          >
            {bannerText}
          </BannerText>
        )}
      </HeaderAndBannerWrapper>
    );
  }

  return (
    <HeaderAndBannerWrapper
      $addExtraPaddingBottom={addExtraPaddingBottom}
      $removeFlexBasis={removeFlexBasis}
    >
      <HeaderWrapper
        $couponTheme={couponTheme}
        $isDisplayMode={isDisplayMode}
        $isDisabled={isDisabled}
      >
        {discountValue && (
          <DiscountValue
            size="px20"
            color="inherit"
            data-testid={`${dataTestId}DiscountValue`}
          >
            {discountValue.value}
            {discountValue.unit}
          </DiscountValue>
        )}
        <Headline
          ref={headlineRef}
          color="inherit"
          data-testid={`${dataTestId}Headline`}
          $reduceFontSize={shouldReduceHeaderFont}
          $isDisabled={isDisabled}
        >
          {headline}
        </Headline>
      </HeaderWrapper>
      {bannerText && (
        <BannerText
          data-testid={`${dataTestId}BannerText`}
          $isAppOnlyCoupon={isOnlyAppCoupon}
          $isDisplayMode={isDisplayMode}
          $isDisabled={isDisabled}
        >
          {bannerText}
        </BannerText>
      )}
    </HeaderAndBannerWrapper>
  );
};

const DISCOUNT_VALUE_PADDING = 14;

const HeaderAndBannerWrapper = styled.div<{
  $addExtraPaddingBottom?: boolean;
  $removeFlexBasis: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex: 1 0 calc(100% - ${FOOTER_HEIGHT}px);

  ${({$addExtraPaddingBottom}) =>
    $addExtraPaddingBottom &&
    css`
      padding-bottom: ${FOOTER_HEIGHT}px;
    `}

  ${({$removeFlexBasis}) =>
    $removeFlexBasis &&
    css`
      flex-basis: auto;
    `}
`;

const DiscountValue = styled(Text)`
  font-weight: 800;
  padding: ${DISCOUNT_VALUE_PADDING}px;
`;

const Headline = styled(Text)<{
  $reduceFontSize?: boolean;
  $removeSidePadding?: boolean;
  $isDisabled?: boolean;
}>`
  line-height: 1.28;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  padding: 0 24px 0 16px;
  overflow-wrap: anywhere;

  ${({$reduceFontSize}) =>
    $reduceFontSize &&
    css`
      line-height: 1.33;
      font-size: ${FONT_SIZES.px12};
    `}

  ${({$removeSidePadding}) =>
    $removeSidePadding &&
    css`
      padding-inline: 0;
    `};

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      color: ${COLORS.Grey1};
    `};
`;

export const StyledCouponTiers = styled(CouponTiers)<{
  $isSingleTier?: boolean;
  $isDisabled?: boolean;
  $isDisplayMode?: boolean;
}>`
  font-size: ${FONT_SIZES.px20};

  ${({$isSingleTier}) =>
    $isSingleTier &&
    css`
      width: auto;
    `}

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      ${TierContainer} {
        color: ${COLORS.Grey1};

        ${TierShape} {
          background-color: ${COLORS.Grey1};
        }
      }
    `}

  ${({$isDisplayMode}) =>
    $isDisplayMode &&
    css`
      display: flex;
    `}
`;

const HeaderWrapper = styled.div<{
  $couponTheme: Coupon_SimpleCoupon_CouponTheme;
  $isDisplayMode?: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 16px;
  color: inherit;
  overflow: hidden;
  background-color: ${({$couponTheme}) => $couponTheme.backgroundColor};
  color: ${({$couponTheme}) => $couponTheme.foregroundColor};
  transition: color, background-color;
  transition-duration: 300ms;
  transition-timing-function: ease-in;
  flex: 1;

  ${({$isDisplayMode}) =>
    $isDisplayMode &&
    css`
      background-color: ${COLORS.BackgroundCoupon};
    `}

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      color: ${COLORS.Grey1};
      background-color: ${COLORS.Grey4};
    `}
`;

const TierHeaderWrapper = styled(HeaderWrapper)<{
  $isSingleTier?: boolean;
}>`
  align-items: flex-start;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  ${({$isSingleTier}) =>
    $isSingleTier &&
    css`
      flex-direction: row-reverse;
      align-items: center;
    `}

  ${Headline} {
    line-height: 1.42;
  }
`;

export const BannerText = styled.div<{
  $isAppOnlyCoupon: boolean;
  $isDisplayMode?: boolean;
  $isDisabled?: boolean;
}>`
  background-color: ${COLORS.PrimaryCoupon};
  color: ${COLORS.White};
  display: flex;
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  justify-content: center;
  line-height: 1.6;
  padding-block: 2px;
  padding-inline: 6px;
  text-align: center;
  transition: color 300ms ease-in;
  height: 20px;

  ${({$isAppOnlyCoupon}) =>
    $isAppOnlyCoupon &&
    css`
      color: ${COLORS.PrimaryCoupon};
      background-color: ${COLORS.BackgroundCoupon};
    `};

  ${({$isDisplayMode}) =>
    $isDisplayMode &&
    css`
      color: ${COLORS.White};
      background-color: ${COLORS.PrimaryCoupon};
    `};

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      background-color: ${COLORS.Grey1};
      color: ${COLORS.White};
    `};
`;
